import React, { useState, useEffect } from 'react';
import { Search, Bot, Loader2, AlertCircle, FileText, RefreshCw, List } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAuthStore } from '../../stores/authStore';

interface ExtractResult {
  id: string;
  url: string;
  texto_extraido: string | null;
  chunk: boolean;
  embedding: boolean;
  vectorizado: boolean;
  error?: string;
  created_at: string;
  asistente?: {
    nombre: string;
  };
}

interface ProgressInfo {
  total: number;
  processed: number;
  errors: number;
  currentDocument?: string;
}

export default function TavilyExtract() {
  const { user } = useAuthStore();
  const [results, setResults] = useState<ExtractResult[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedResults, setSelectedResults] = useState<Set<string>>(new Set());
  const [progress, setProgress] = useState<ProgressInfo | null>(null);

  useEffect(() => {
    fetchResults();
  }, []);

  const fetchResults = async () => {
    try {
      setIsLoading(true);
      setError('');

      const { data, error } = await supabase
        .from('tavily_extract_results')
        .select(`
          *,
          asistente:asistentes(nombre)
        `)
        .eq('chunk', false)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setResults(data || []);
    } catch (error: any) {
      console.error('Error fetching results:', error);
      setError('Error al cargar los resultados');
    } finally {
      setIsLoading(false);
    }
  };

  const handleChunking = async () => {
    if (selectedResults.size === 0) {
      setError('Selecciona al menos un documento para procesar');
      return;
    }

    try {
      setIsProcessing(true);
      setError('');

      // Initialize progress
      setProgress({
        total: selectedResults.size,
        processed: 0,
        errors: 0
      });

      const selectedDocs = results.filter(result => selectedResults.has(result.id));
      let processedCount = 0;
      let errorCount = 0;

      for (const result of selectedDocs) {
        if (!result.texto_extraido || result.chunk) continue;

        try {
          // Update progress
          setProgress(prev => prev ? {
            ...prev,
            currentDocument: result.url
          } : null);

          // Split text into chunks of approximately 1000 characters
          const chunks = result.texto_extraido.match(/[^.!?]+[.!?]+/g) || [result.texto_extraido];
          const chunkSize = 1000;
          let currentChunk = '';
          const finalChunks: string[] = [];

          for (const sentence of chunks) {
            if ((currentChunk + sentence).length <= chunkSize) {
              currentChunk += sentence;
            } else {
              if (currentChunk) finalChunks.push(currentChunk.trim());
              currentChunk = sentence;
            }
          }
          if (currentChunk) finalChunks.push(currentChunk.trim());

          // Create chunks in database
          for (let i = 0; i < finalChunks.length; i++) {
            const { error: chunkError } = await supabase
              .from('chunks')
              .insert({
                texto: finalChunks[i],
                orden: i,
                extract_result_id: result.id,
                thread_id: crypto.randomUUID(),
                embedding: false,
                processed: false
              });

            if (chunkError) throw chunkError;
          }

          // Mark document as chunked
          const { error: updateError } = await supabase
            .from('tavily_extract_results')
            .update({ chunk: true })
            .eq('id', result.id);

          if (updateError) throw updateError;

          // Remove from local state
          setResults(prev => prev.filter(r => r.id !== result.id));
          processedCount++;

          // Update progress
          setProgress(prev => prev ? {
            ...prev,
            processed: prev.processed + 1
          } : null);

        } catch (error: any) {
          console.error(`Error processing document ${result.id}:`, error);
          errorCount++;
          setProgress(prev => prev ? {
            ...prev,
            errors: prev.errors + 1
          } : null);
        }
      }

      setSuccess(`Proceso completado. ${processedCount} documentos procesados. ${errorCount} errores.`);
      setSelectedResults(new Set());

    } catch (error: any) {
      console.error('Error in chunking process:', error);
      setError(`Error en el proceso de chunking: ${error.message}`);
    } finally {
      setIsProcessing(false);
      setProgress(null);
    }
  };

  const toggleResultSelection = (resultId: string) => {
    setSelectedResults(prev => {
      const next = new Set(prev);
      if (next.has(resultId)) {
        next.delete(resultId);
      } else {
        next.add(resultId);
      }
      return next;
    });
  };

  const selectAllResults = () => {
    setSelectedResults(new Set(filteredResults.map(r => r.id)));
  };

  const deselectAllResults = () => {
    setSelectedResults(new Set());
  };

  const filteredResults = results.filter(result => {
    if (searchTerm) {
      return (
        result.url.toLowerCase().includes(searchTerm.toLowerCase()) ||
        result.asistente?.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
        result.texto_extraido?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    return true;
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-800">Extracción de Textos</h1>
        <div className="flex items-center space-x-4">
          <button
            onClick={handleChunking}
            disabled={selectedResults.size === 0 || isProcessing}
            className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:opacity-50 flex items-center space-x-2"
          >
            {isProcessing ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                <span>Procesando...</span>
              </>
            ) : (
              <>
                <List className="w-5 h-5" />
                <span>Chunking ({selectedResults.size})</span>
              </>
            )}
          </button>

          <button
            onClick={fetchResults}
            disabled={isProcessing}
            className="p-2 text-gray-600 hover:bg-gray-100 rounded-full"
            title="Actualizar resultados"
          >
            <RefreshCw className={`w-5 h-5 ${isProcessing ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded flex items-center">
          <AlertCircle className="w-5 h-5 mr-2" />
          {error}
        </div>
      )}

      {success && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded">
          {success}
        </div>
      )}

      {isProcessing && progress && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-medium text-blue-900">Procesando Documentos</h3>
            <div className="text-sm text-blue-700">
              {progress.processed} / {progress.total} completados
            </div>
          </div>
          
          <div className="w-full bg-blue-200 rounded-full h-2.5 mb-2">
            <div 
              className="bg-blue-600 h-2.5 rounded-full transition-all duration-300"
              style={{ width: `${(progress.processed / progress.total) * 100}%` }}
            ></div>
          </div>

          {progress.currentDocument && (
            <div className="text-sm text-blue-700 truncate">
              Procesando: {progress.currentDocument}
            </div>
          )}

          {progress.errors > 0 && (
            <div className="text-sm text-red-600 mt-1">
              Errores: {progress.errors}
            </div>
          )}

          <div className="text-xs text-blue-600 mt-2">
            No cierres esta ventana hasta que el proceso haya terminado
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="relative flex-1">
            <input
              type="text"
              placeholder="Buscar resultados..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={isProcessing}
            />
            <Search className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
          </div>
          <div className="flex items-center space-x-2 ml-4">
            <button
              onClick={selectAllResults}
              className="flex items-center px-3 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded-md"
              disabled={isProcessing}
            >
              Seleccionar todos
            </button>
            <button
              onClick={deselectAllResults}
              className="flex items-center px-3 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded-md"
              disabled={isProcessing}
            >
              Deseleccionar todos
            </button>
          </div>
        </div>

        <div className="space-y-4">
          {filteredResults.map((result) => (
            <div key={result.id} className="border rounded-lg p-4">
              <div className="flex justify-between items-start mb-2">
                <div className="flex items-center space-x-4">
                  <input
                    type="checkbox"
                    checked={selectedResults.has(result.id)}
                    onChange={() => toggleResultSelection(result.id)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    disabled={isProcessing}
                  />
                  <div>
                    <a
                      href={result.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline font-semibold"
                    >
                      {result.url}
                    </a>
                    <div className="text-sm text-gray-500 mt-1">
                      <div className="flex items-center space-x-2">
                        <Bot className="w-4 h-4" />
                        <span>Asistente: {result.asistente?.nombre}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {result.error ? (
                <div className="text-red-600 text-sm mt-2">
                  Error: {result.error}
                </div>
              ) : result.texto_extraido ? (
                <div className="mt-2 text-gray-600 text-sm">
                  <div className="max-h-32 overflow-y-auto">
                    {result.texto_extraido}
                  </div>
                </div>
              ) : (
                <div className="text-gray-500 text-sm mt-2 italic">
                  Sin texto extraído
                </div>
              )}

              <div className="text-xs text-gray-400 mt-2">
                {new Date(result.created_at).toLocaleString()}
              </div>
            </div>
          ))}

          {filteredResults.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No se encontraron resultados sin procesar</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}